<script setup lang="ts">
import { computed, h, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DenseTable from '@/components/ui/DenseTable.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Paginator from 'primevue/paginator'
import Select from 'primevue/select'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/auth'
import { roleLabel, roleSeverity } from '@/utils/roles'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useFiltrosEnUrl } from '@/composables/useFiltrosEnUrl'
import { formatShortDate } from '@/utils/format'
import { usersApi, ASSIGNABLE_ROLES, type CrmUser } from '@/services/users'

/**
 * Directorio de usuarios del CRM.
 *
 * Muestra el estado real de cada cuenta, no solo quién existe: un usuario
 * desactivado sigue teniendo sus cotizaciones, así que borrarlo de la lista haría
 * que el ranking de asesores dejara de cuadrar sin explicación. Por eso la baja
 * es una desactivación y la columna "Estado" está siempre visible.
 */

const router = useRouter()
const authStore = useAuthStore()
const layout = useBreakpoint()

const isDesktop = computed(() => layout.value === 'desktop')
const canManage = computed(() => authStore.hasPermission('users.manage'))

const items = ref<CrmUser[]>([])
const loading = ref(false)
const errorMessage = ref<string | null>(null)
const search = ref('')
const role = ref<string>('')
const onlyActive = ref<boolean | null>(null)
const page = ref(1)
const total = ref(0)
const perPage = 20

/*
| Los filtros viven en la URL
|--------------------------------------------------------------------------
|
| Filtrabas los usuarios, abías uno, volvías, y el filtro había desaparecido. No era el botón
| de atrás: es que los filtros estaban en `ref`s de este componente, y un componente no
| sobrevive a que te vayas a otra vista.
|
| `soloActivos` es un sí/no, así que viaja como `1` y `0`, no como texto: un `?soloActivos=si`
| en la URL sería una tercera opción que no existe en el control.
*/
const filtros = useFiltrosEnUrl({
  busqueda: { ref: search, texto: true },
  rol: { ref: role, permitidos: valoresDeRol() },
  soloActivos: { ref: onlyActive, permitidos: [true, false, null], vacio: null },
  pagina: { ref: page, numero: true },
})

/** Los roles que el control ofrece, para validar lo que llega por la URL. */
function valoresDeRol(): Array<string | number> {
  return ASSIGNABLE_ROLES.map((valor) => valor)
}

const roleOptions = computed(() => [
  { value: '', label: 'Todos los roles' },
  ...ASSIGNABLE_ROLES.map((value) => ({
    value,
    label: roleLabel(value),
  })),
])

/**
 * Filas de la tabla de escritorio.
 *
 * Las etiquetas se precalculan porque `DenseTable` imprime `row[field]`. Los
 * `null` se convierten en "—" porque una celda vacía no se distingue de un dato
 * vacío de verdad, y "Nunca" en la última columna evita que un usuario sin
 * ingresos parezca un usuario sin datos.
 */
const rows = computed(() =>
  items.value.map((user) => ({
    ...user,
    name_label: user.name,
    email_label: user.email ?? '—',
    role_label: user.roles.length === 0 ? '—' : roleLabel(user.roles[0]),
    state_label: user.active ? 'ACTIVO' : 'INACTIVO',
    last_login_label: user.last_login_at === null ? 'Nunca' : formatShortDate(user.last_login_at),
    is_self: user.id === authStore.user?.id,
    actions: [
      { label: `Editar ${user.name}`, value: 'edit', icon: 'pi pi-pencil' },
      { label: `Ver ${user.name}`, value: 'view', icon: 'pi pi-eye' },
    ],
  })),
)

/**
 * Columnas de la tabla densa.
 *
 * El estado no se pliega en móvil aunque la tabla no se use allí: es el dato que
 * distingue una cuenta que entra de una que no, y por eso tampoco se esconde en
 * escritorio. El email sí, porque en una pantalla estrecha obliga a scroll
 * horizontal para llegar al estado, que es lo que hay que leer.
 */
/**
 * Celda del nombre, con la marca de la propia cuenta.
 *
 * Va como `template` de la columna y no como etiqueta porque el "(tú)" es un
 * elemento aparte con su propio color, no texto seguido. `DenseTable` solo sabe
 * imprimir `row[field]`, así que lo que necesita más de un nodo va aquí.
 */
function nameCell(row: unknown): unknown {
  const user = row as CrmUser

  return h('span', { class: 'flex flex-col gap-0.5' }, [
    h('span', { class: 'flex items-center gap-2' }, [
      h('span', { class: 'font-medium' }, user.name),
      user.id === authStore.user?.id
        ? h('span', { class: 'text-xs font-normal text-surface-500' }, '(tú)')
        : null,
    ]),
    user.email === null
      ? null
      : h('span', { class: 'text-xs font-normal text-surface-500' }, user.email),
  ])
}

/**
 * La celda del estado, con su color.
 *
 * `DenseTable` solo sabe imprimir `row[field]`, así que una celda que necesita más
 * que texto va por `template`. Antes era texto pelado: el color del estado solo
 * existía en las tarjetas de móvil, y la mitad de los usuarios está en escritorio y
 * no lo tenía.
 *
 * Verde para activo y ámbar para inactivo, no gris. Un usuario desactivado es algo
 * que hay que notar, no una fila más.
 */
function stateCell(row: unknown): unknown {
  const user = row as CrmUser

  return h(Tag, {
    value: user.active ? 'ACTIVO' : 'INACTIVO',
    severity: user.active ? 'success' : 'warn',
  })
}

const visibleColumns = [
  { field: 'name_label', header: 'Nombre', template: nameCell },
  { field: 'username', header: 'Usuario' },
  { field: 'role_label', header: 'Rol' },
  { field: 'state_label', header: 'Estado', template: stateCell },
  { field: 'last_login_label', header: 'Último acceso', hideOnMobile: true },
]

const isEmpty = computed(() => !loading.value && errorMessage.value === null && items.value.length === 0)
const hasFilters = computed(() => search.value.trim() !== '' || role.value !== '' || onlyActive.value !== null)

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el directorio de usuarios. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await usersApi.list({
      search: search.value.trim() || undefined,
      role: role.value === '' ? undefined : role.value,
      active: onlyActive.value ?? undefined,
      page: page.value,
      per_page: perPage,
    })

    items.value = data.data
    total.value = data.meta.total
  } catch (cause: unknown) {
    items.value = []
    total.value = 0
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

let debounceHandle: ReturnType<typeof setTimeout> | null = null

watch(search, () => {
  page.value = 1
  if (debounceHandle !== null) clearTimeout(debounceHandle)
  // Sin debounce, cada tecla dispara una petición y las respuestas pueden llegar
  // desordenadas, pintando resultados de un término anterior.
  debounceHandle = setTimeout(() => {
    void load()
  }, 300)
})

watch([role, onlyActive], () => {
  page.value = 1
  void load()
})

watch(page, () => {
  void load()
})

onMounted(() => {
  if (!canManage.value) return

  void load()
})

onBeforeUnmount(() => {
  // El temporizador del debounce sobrevive al desmontaje si no se limpia, y
  // dispara una petición que no le sirve a nadie. Aquí se nota de forma clara:
  // el test siguiente recibe una llamada que su vista nunca hizo.
  if (debounceHandle !== null) {
    clearTimeout(debounceHandle)
    debounceHandle = null
  }
})

function clearFilters(): void {
  filtros.limpiar()
  page.value = 1
  void load()
}

function onPageChange(event: { page: number }): void {
  page.value = event.page + 1
}

function openUser(user: CrmUser, action: 'view' | 'edit' = 'view'): void {
  if (action === 'edit') {
    void router.push(filtros.conQuery({ name: 'users.edit', params: { id: String(user.id) } }))
    return
  }

  void router.push(filtros.conQuery({ name: 'users.show', params: { id: String(user.id) } }))
}

/** `DenseTable` entrega `unknown`; aquí se estrecha y se decide la acción. */
function handleRow(row: unknown): void {
  const user = row as CrmUser
  const action = (row as { actions?: Array<{ value: string }> }).actions?.[0]?.value

  openUser(user, action === 'edit' ? 'edit' : 'view')
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Usuarios
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          Cuentas con acceso al CRM y sus permisos
        </p>
      </div>
      <Button
        v-if="canManage"
        label="Nuevo"
        icon="pi pi-plus"
        @click="router.push({ name: 'users.create' })"
      />
    </header>

    <Message
      v-if="!canManage"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      La gestión de usuarios es de acceso restringido: tu rol no tiene el permiso
      <code>users.manage</code>.
    </Message>

    <template v-else>
      <AppCard>
        <template #header>
          <form
            class="flex flex-col gap-3 sm:flex-row"
            role="search"
            @submit.prevent
          >
            <InputText
              v-model="search"
              placeholder="Buscar por nombre, usuario o email"
              aria-label="Buscar usuarios"
              class="w-full"
            />
            <Select
              v-model="role"
              :options="roleOptions"
              option-label="label"
              option-value="value"
              placeholder="Rol"
              aria-label="Filtrar por rol"
              class="w-full sm:w-48"
            />
            <Select
              v-model="onlyActive"
              :options="[
                { value: null, label: 'Activos e inactivos' },
                { value: true, label: 'Solo activos' },
                { value: false, label: 'Solo inactivos' },
              ]"
              option-label="label"
              option-value="value"
              placeholder="Estado"
              aria-label="Filtrar por estado"
              class="w-full sm:w-48"
            />
            <Button
              v-if="hasFilters"
              label="Limpiar"
              icon="pi pi-filter-slash"
              severity="secondary"
              text
              @click="clearFilters"
            />
          </form>
        </template>

        <div
          v-if="errorMessage"
          class="flex flex-col items-center gap-3 py-12 text-center"
          role="alert"
        >
          <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
          <p class="font-medium text-surface-900 dark:text-surface-100">
            No se pudo cargar el directorio
          </p>
          <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
            {{ errorMessage }}
          </p>
          <Button
            label="Reintentar"
            icon="pi pi-refresh"
            severity="secondary"
            @click="load"
          />
        </div>

        <div
          v-else-if="loading"
          class="py-12 text-center text-surface-500"
          aria-busy="true"
        >
          <i class="pi pi-spin pi-spinner text-2xl" />
          <p class="mt-2 text-sm">
            Cargando usuarios…
          </p>
        </div>

        <div
          v-else-if="isEmpty"
          class="py-12 text-center"
        >
          <i class="pi pi-inbox text-5xl text-surface-300 dark:text-surface-600" />
          <h2 class="mt-3 text-lg font-medium text-surface-900 dark:text-surface-100">
            {{ hasFilters ? 'Ningún usuario coincide' : 'No hay usuarios' }}
          </h2>
          <p class="mt-1 text-surface-500 dark:text-surface-400">
            <template v-if="hasFilters">
              Prueba a cambiar la búsqueda o a quitar los filtros.
            </template>
            <template v-else>
              Crea la primera cuenta para que alguien pueda entrar al CRM.
            </template>
          </p>
          <div class="mt-4 flex justify-center gap-2">
            <Button
              v-if="hasFilters"
              label="Limpiar filtros"
              icon="pi pi-filter-slash"
              severity="secondary"
              @click="clearFilters"
            />
            <Button
              v-else
              label="Crear el primero"
              icon="pi pi-plus"
              @click="router.push({ name: 'users.create' })"
            />
          </div>
        </div>

        <template v-else>
          <!-- Escritorio: `DenseTable`, el mismo componente que usan catálogo y
               clientes. Con una tabla escrita a mano las filas de esta sección
               medían distinto de las de las demás. -->
          <DenseTable
            v-if="isDesktop"
            :columns="visibleColumns"
            :rows="rows"
            :loading="loading"
            :paginator="false"
            @row-click="handleRow"
            @action="handleRow"
          />

          <ul
            v-else
            class="space-y-3"
            data-testid="users-cards"
          >
            <li
              v-for="user in items"
              :key="user.id"
            >
              <button
                type="button"
                class="w-full rounded-lg border border-surface-200 bg-white p-3 text-left transition-colors hover:bg-surface-50 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-surface-700 dark:bg-surface-900 dark:hover:bg-surface-800 dark:focus-visible:ring-offset-surface-900"
                @click="openUser(user)"
              >
                <div class="flex items-start justify-between gap-2">
                  <span class="min-w-0 truncate font-medium text-surface-900 dark:text-surface-100">
                    {{ user.name }}
                  </span>
                  <!--
                    La misma palabra y el mismo color que la tabla. Antes ponía «Activo»
                    en minúsculas y ámbar `danger` para un usuario desactivado, que se
                    lee como un error cuando solo está dado de baja.
                  -->
                  <Tag
                    :value="user.active ? 'ACTIVO' : 'INACTIVO'"
                    :severity="user.active ? 'success' : 'warn'"
                  />
                </div>
                <p class="mt-1 font-mono text-xs text-surface-500">
                  {{ user.username }}
                </p>
                <div class="mt-2">
                  <Tag
                    :value="user.roles.includes('admin') ? 'Administrador' : 'Asesor'"
                    :severity="user.roles.includes('admin') ? 'info' : 'secondary'"
                  />
                </div>
              </button>
            </li>
          </ul>

          <Paginator
            v-if="total > perPage"
            :first="(page - 1) * perPage"
            :rows="perPage"
            :total-records="total"
            :rows-per-page-options="[20]"
            class="mt-4"
            @page="onPageChange"
          />
        </template>
      </AppCard>
    </template>
  </div>
</template>
